import NextLink from 'next/link';
import styled, { css } from 'styled-components';

import { ChevronUpIc, ArrowBackIc } from '@dsch/dd-icons';

import { Link } from 'components/Toolkit/Button/Link';

import { useSectionBrower } from 'components/SearchPage/components/SectionBrowser/hooks/useSectionBrowser';

import { formatNumber } from 'helpers/formatting';

export interface ISection {
  name: string;
  displayName: string;
  searchMatches?: number;
}

export interface ISectionBrowserProps {
  parentSection: ISection | null;
  currentSection: ISection;
  sections: ISection[];
  onSelectSection: (section: string) => void;
  onSelectBack: (section: string) => void;
  collapsible?: boolean;
  baseUrl?: string;
}

const Item = styled(Link)<{ selected: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M16}`};
  ${({ theme }) => theme.fontSize.M14};
  border-bottom: 1px solid ${({ theme }) => theme.colors.GREY_LIGHTER};
  color: ${({ theme }) => theme.colors.GREY_DARKER};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.GREY_LIGHTER};
    color: ${({ theme }) => theme.colors.GREY_DARKER};
  }

  border-bottom: none;

  ${({ selected, theme }) =>
    selected &&
    css`
      font-weight: bold;
      background: ${({ theme }) => theme.colors.WHITE} !important;
      border: 2px solid ${theme.colors.GREY_DARKER};
      border-radius: ${({ theme }) => theme.borderRadius.default};
      box-shadow: ${({ theme }) => theme.shadow.CONTAINER};
    `};
`;

const Count = styled.span`
  color: ${({ theme }) => theme.colors.GREY};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
`;

const Back = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M16}`};
  font-weight: bold;
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.GREY_DARKER};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.GREY_LIGHTER};
    color: ${({ theme }) => theme.colors.GREY_DARKER};
  }
`;

const BackArrow = styled(ArrowBackIc)`
  margin-right: ${({ theme }) => theme.spacing.S8};
`;

const SubsectionsHeading = styled.p`
  padding: ${({ theme }) => `0 ${theme.spacing.M16}`};
  font-weight: bold;
  ${({ theme }) => theme.fontSize.S12};
  background: ${({ theme }) => theme.colors.GREY_LIGHTER};
  color: ${({ theme }) => theme.colors.GREY};
`;

const Collapsible = styled.div<{ collapsed: boolean }>`
  position: relative;
  ${({ collapsed }) =>
    collapsed &&
    css`
      height: 400px;
      overflow: hidden;
    `}
`;

const CollapseButtonIcon = styled(ChevronUpIc)`
  display: inline-block;
  margin-left: ${({ theme }) => theme.spacing.S8};
`;

const CollapseButton = styled.button<{ collapsed: boolean }>`
  position: sticky;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.S8};
  ${({ theme }) => theme.fontSize.M14};
  text-align: center;
  background: linear-gradient(
    350.17deg,
    #fafafa 50.57%,
    rgba(250, 250, 250, 0) 130.53%
  );
  color: ${({ theme }) => theme.colors.BLUE};
  cursor: pointer;

  ${({ collapsed }) =>
    collapsed &&
    css`
      ${CollapseButtonIcon} {
        transform: rotate(180deg);
      }
    `}
`;

interface ISectionProps {
  link: string;
  section: ISection;
  selected: boolean;
  onClick?: (name: string) => void;
}

function Section({ link, section, selected, onClick }: ISectionProps) {
  return (
    <Item
      href={link}
      selected={selected}
      onClick={() => onClick && onClick(section.name)}
      NextLink={NextLink}
      ofType="SECONDARY"
      shallow
    >
      <span>{section.displayName}</span>
      {typeof section.searchMatches !== 'undefined' && (
        <Count>{formatNumber(section.searchMatches)}</Count>
      )}
    </Item>
  );
}

interface IBackButtonProps {
  link: string;
  section: ISection;
  onClick: () => void;
}
function BackButton({ link, section, onClick }: IBackButtonProps) {
  return (
    <Back
      href={link}
      onClick={onClick}
      NextLink={NextLink}
      ofType="SECONDARY"
      shallow
    >
      <BackArrow width={12} height={12} />
      <span>{section.displayName}</span>
    </Back>
  );
}

function SectionBrowser({
  sections,
  currentSection,
  parentSection,
  onSelectSection,
  onSelectBack,
  collapsible = true,
  baseUrl,
}: ISectionBrowserProps) {
  const {
    collapsed,
    setCollapsed,
    handleSelectBack,
    shouldCollapse,
    handleSelectSection,
    getLink,
    showSubsections,
    filteredSubsections,
  } = useSectionBrower({
    sections,
    parentSection,
    onSelectSection,
    onSelectBack,
    collapsible,
    baseUrl,
  });

  return (
    <div>
      {parentSection && (
        <div>
          <BackButton
            link={getLink(parentSection.name)}
            section={parentSection}
            onClick={handleSelectBack}
          />
          <SubsectionsHeading>Subsections</SubsectionsHeading>
        </div>
      )}
      <Collapsible collapsed={shouldCollapse ? collapsed : false}>
        <ul>
          <li>
            <Section
              link={getLink(currentSection.name)}
              selected={true}
              section={currentSection}
            />
          </li>
          {showSubsections &&
            filteredSubsections.map((item, index) => (
              <li key={index}>
                <Section
                  link={getLink(item.name)}
                  selected={false}
                  section={item}
                  onClick={() => handleSelectSection(item.name)}
                />
              </li>
            ))}
        </ul>
        {shouldCollapse && (
          <CollapseButton
            collapsed={collapsed}
            onClick={() => setCollapsed(!collapsed)}
          >
            <span>
              {collapsed
                ? `View all ${filteredSubsections.length} sections`
                : 'View less sections'}
            </span>
            <CollapseButtonIcon width={16} height={16} />
          </CollapseButton>
        )}
      </Collapsible>
    </div>
  );
}

export { SectionBrowser };
